import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'localization';
import { useNavigate, useLocation } from 'react-router-dom';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import { ApproachDeploymentInput, ApproachReference } from 'types/approach';
import { UnifiedFilters } from 'types/filters';
import { useApproachDetail } from '../../../hooks/useApproachDetail';
import { useApproachVersions } from '../../../hooks/useApproachVersions';
import { usePublisherName } from '../../../hooks/usePublisherName';
import { useRatings } from '../../../hooks/useRatings';
import { useSubmitRating } from '../../../hooks/useSubmitRating';
import {
  previewApproachDeployment,
  deployApproach,
  createSphere,
} from '../../../api/marketplaceApi';
import type { TemplateDeployParams } from '../../../components/organisms/marketplace/ApproachDeployWizardOrganism';
import ButtonAtom from '../../../components/atoms/Button/ButtonAtom';
import ApproachDetailTabsAtom from '../../../components/atoms/marketplace/ApproachDetailTabsAtom';
import SectionHeaderAtom from '../../../components/atoms/marketplace/SectionHeaderAtom';
import VersionSelectorAtom from '../../../components/atoms/marketplace/VersionSelectorAtom';
import ApproachHeaderMolecule from '../../../components/molecules/marketplace/ApproachHeaderMolecule';
import OutcomeCardsMolecule from '../../../components/molecules/marketplace/OutcomeCardsMolecule';
import { deriveOutcomes } from '../../../utils/marketplace/deriveOutcomes';
import ApplicabilityRendererMolecule from '../../../components/molecules/marketplace/ApplicabilityRendererMolecule';
import EdgeCaseCalloutMolecule, {
  type EdgeCaseSeverity,
} from '../../../components/molecules/marketplace/EdgeCaseCalloutMolecule';
import DeployReadinessChecklistMolecule, {
  type ReadinessItem,
} from '../../../components/molecules/marketplace/DeployReadinessChecklistMolecule';
import DeployBehaviorMolecule, {
  type ConflictStrategy,
} from '../../../components/molecules/marketplace/DeployBehaviorMolecule';
import type { ApproachDetailTab } from '../../../components/atoms/marketplace/ApproachDetailTabsAtom';
import RatingSummaryMolecule from '../../../components/molecules/marketplace/RatingSummaryMolecule';
import RatingFormMolecule from '../../../components/molecules/marketplace/RatingFormMolecule';
import type { RatingFormSubmitData } from '../../../components/molecules/marketplace/RatingFormMolecule';
import ReviewListMolecule from '../../../components/molecules/marketplace/ReviewListMolecule';
import type { ReviewItem } from '../../../components/molecules/marketplace/ReviewListMolecule';
import DeployTargetDialogOrganism from '../../../components/organisms/marketplace/DeployTargetDialogOrganism';
import WhatsIncludedReaderOrganism from '../../../components/organisms/marketplace/WhatsIncludedReaderOrganism';
import ApproachDeployWizardOrganism from '../../../components/organisms/marketplace/ApproachDeployWizardOrganism';
import { ItemDetailDialogOrganism } from '../../../components/organisms/marketplace/ItemDetailDialogOrganism';
import { useChannelItemManageAccess } from '../../../hooks/useChannelItemManageAccess';
import { getLocalizedText } from '../../../utils/getLocalizedText';
import './ApproachDetailPageOrganism.scss';

export interface ApproachDetailPageOrganismProps {
  systemName: string;
  currentSphereId?: string;
}

const ApproachDetailPageOrganism: React.FC<ApproachDetailPageOrganismProps> = ({
  systemName,
  currentSphereId,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const channelContext = (location.state as { channelId?: string; channelName?: string } | null);
  const apiClients = useApiClientsOrNull();

  // Tab state
  const [activeTab, setActiveTab] = useState<ApproachDetailTab>('overview');

  // Version selector state
  const [selectedVersion, setSelectedVersion] = useState<number | undefined>(undefined);

  // Deploy flow state
  const [deploymentInputs, setDeploymentInputs] = useState<ApproachDeploymentInput[]>([]);
  const [showWizard, setShowWizard] = useState(false);
  const [isDeploying, setIsDeploying] = useState(false);
  const [deployResult, setDeployResult] = useState<{
    success: boolean;
    message?: string;
  } | null>(null);
  const [deployError, setDeployError] = useState<string | null>(null);
  const [deployingMessage, setDeployingMessage] = useState<string | undefined>(undefined);

  // Target picker state (shown when current sphere doesn't match applicability)
  const [showTargetPicker, setShowTargetPicker] = useState(false);
  const [targetSphereId, setTargetSphereId] = useState<string | null>(null);
  const [targetSphereName, setTargetSphereName] = useState<string | null>(null);
  const [pendingDeployApplicability, setPendingDeployApplicability] =
    useState<UnifiedFilters | null>(null);

  const { approach, version, loading, error, refetch } =
    useApproachDetail(systemName, selectedVersion);
  const { versions } = useApproachVersions(systemName);
  const { publisherName, isSystem: isSystemPublisher } = usePublisherName(
    approach?.tenant_id
  );

  // Manage affordance — surfaces when the current user has `manage` on a
  // channel_item publishing this approach. Opens the item detail dialog.
  const { canManage, manageableItem } = useChannelItemManageAccess({
    itemType: 'approach',
    itemSystemName: approach?.system_name,
  });
  const [manageDialogOpen, setManageDialogOpen] = useState(false);

  // Dynamic page title
  useEffect(() => {
    if (approach) {
      const name = getLocalizedText(approach.name);
      document.title = `${name} - Marketplace - Center`;
    }
    return () => { document.title = 'Center'; };
  }, [approach]);

  const {
    ratings,
    total: ratingsTotal,
    userRating,
    loading: ratingsLoading,
    hasMore: ratingsHasMore,
    loadMore: loadMoreRatings,
    refetch: refetchRatings,
  } = useRatings('approach', systemName);

  const { submit: submitRatingFn, submitting } = useSubmitRating(
    undefined,
    undefined,
    undefined
  );

  const [showRatingForm, setShowRatingForm] = useState(false);

  const handleRatingSubmit = useCallback(
    async (data: RatingFormSubmitData) => {
      const result = await submitRatingFn({
        targetType: 'approach',
        targetId: systemName,
        stars: data.stars,
        review: data.reviewBody
          ? { title: data.reviewTitle || null, body: data.reviewBody }
          : undefined,
      });
      if (result) {
        setShowRatingForm(false);
        refetchRatings();
      }
    },
    [systemName, submitRatingFn, refetchRatings]
  );

  const reviewItems: ReviewItem[] = useMemo(() => {
    return ratings
      .filter((r) => r.review)
      .map((r) => ({
        id: r.id,
        stars: r.stars,
        title: r.review?.title,
        body: r.review?.body || '',
        authorName: r.user_id,
        date: r.created_at,
        isEdited: r.review?.is_edited,
      }));
  }, [ratings]);

  const definition = version?.definition ?? null;
  const outcomesCount = useMemo(
    () => (definition ? deriveOutcomes(definition).length : 0),
    [definition],
  );

  // Always show the "What's included?" tab when the approach has any
  // definition at all. Empty buckets are handled by the reader; an approach
  // with only an applicability statement (e.g. "deployable to any sphere")
  // still gets the tab so the surface is consistent across approaches.
  const hasWhatsIncludedContent = !!definition;

  const applicabilityFilters: UnifiedFilters | null = useMemo(() => {
    return (
      version?.applicability ??
      ((version?.definition as unknown as { applicability?: UnifiedFilters })
        ?.applicability ??
        null)
    );
  }, [version]);

  const requiredInputs: ReadinessItem[] = useMemo(() => {
    return (definition?.deploymentInputs ?? [])
      .filter((input: ApproachDeploymentInput) => input.required)
      .map((input: ApproachDeploymentInput) => ({
        id: input.id,
        label: getLocalizedText(input.label) || input.id,
        description: input.description
          ? getLocalizedText(input.description) || undefined
          : undefined,
      }));
  }, [definition?.deploymentInputs]);

  const optionalInputs: ReadinessItem[] = useMemo(() => {
    return (definition?.deploymentInputs ?? [])
      .filter((input: ApproachDeploymentInput) => !input.required)
      .map((input: ApproachDeploymentInput) => ({
        id: input.id,
        label: getLocalizedText(input.label) || input.id,
        description: input.description
          ? getLocalizedText(input.description) || undefined
          : undefined,
      }));
  }, [definition?.deploymentInputs]);

  const dependencyItems: ReadinessItem[] = useMemo(() => {
    return (definition?.subApproaches ?? [])
      .filter((ref: ApproachReference) => ref.mode === 'dependency')
      .map((ref: ApproachReference) => ({
        id: ref.approachSystemName,
        label: ref.approachSystemName,
      }));
  }, [definition?.subApproaches]);

  const hasReadiness =
    requiredInputs.length > 0 ||
    optionalInputs.length > 0 ||
    dependencyItems.length > 0;

  const deployBehavior = useMemo(() => {
    const settings = definition?.deploymentSettings;
    if (!settings) return null;
    // 'ask' means "prompt the user at deploy time" — no static behavior to depict.
    // Hide the bucket rather than mis-render as 'fail' / 'skip'.
    if (settings.conflictStrategy === 'ask') return null;
    const CONFLICT_MAP: Record<'skip' | 'override', ConflictStrategy> = {
      skip: 'skip',
      override: 'overwrite',
    };
    return {
      conflictStrategy: CONFLICT_MAP[settings.conflictStrategy],
      rollback: !!settings.allowRollback,
      cascade:
        !!settings.autoCreateChildren ||
        (typeof settings.maxCascadeDepth === 'number' &&
          settings.maxCascadeDepth > 0),
    };
  }, [definition?.deploymentSettings]);

  const edgeCases: Array<{
    id: string;
    severity: EdgeCaseSeverity;
    title: string;
    body: string;
  }> = useMemo(() => {
    // V1: no authored edge case content on ApproachDefinition. Bucket hides
    // gracefully when empty. Future iterations can derive callouts here.
    return [];
  }, []);

  // Continue deploy flow with a resolved sphere: decide wizard vs immediate deploy
  const proceedWithDeploy = useCallback(
    async (sphereId: string) => {
      if (!apiClients?.graphClient) return;

      setIsDeploying(true);

      try {
        const preview = await previewApproachDeployment(
          apiClients.graphClient,
          systemName,
          sphereId
        );

        const hasInputs = (preview.deploymentInputs || []).length > 0;
        const hasTemplate = Boolean(definition?.templateFor);

        if (hasInputs || hasTemplate) {
          setDeploymentInputs(preview.deploymentInputs || []);
          setIsDeploying(false);
          setShowWizard(true);
        } else {
          const result = await deployApproach(apiClients.graphClient, {
            sphereId,
            approachSystemName: systemName,
          });
          setIsDeploying(false);
          setDeployResult({
            success: result.status !== 'failed',
            message: result.status === 'failed'
              ? t('marketplace.deploy.error')
              : t('marketplace.deploy.success'),
          });
        }
      } catch {
        setIsDeploying(false);
        setDeployError(t('marketplace.deploy.previewError'));
      }
    },
    [apiClients, systemName, definition?.templateFor, t]
  );

  // Deploy flow: always show target picker so user can choose/confirm the sphere
  const handleDeploy = useCallback(async () => {
    if (!apiClients?.graphClient) return;

    setDeployError(null);
    setDeployResult(null);
    setPendingDeployApplicability(version?.applicability || (version?.definition as any)?.applicability || null);
    setShowTargetPicker(true);
  }, [apiClients, version]);

  const handleWizardDeploy = useCallback(
    async (values: Record<string, unknown>) => {
      const effectiveSphereId = targetSphereId || currentSphereId;
      if (!apiClients?.graphClient || !effectiveSphereId) return;

      setIsDeploying(true);
      try {
        const result = await deployApproach(apiClients.graphClient, {
          sphereId: effectiveSphereId,
          approachSystemName: systemName,
          deploymentInputValues: values,
        });

        setDeployResult({
          success: result.status !== 'failed',
          message: result.status === 'failed'
            ? t('marketplace.deploy.error')
            : t('marketplace.deploy.success'),
        });
      } catch {
        setDeployResult({
          success: false,
          message: t('marketplace.deploy.error'),
        });
      } finally {
        setIsDeploying(false);
      }
    },
    [apiClients, currentSphereId, targetSphereId, systemName, t]
  );

  const handleTemplateDeploy = useCallback(
    async (params: TemplateDeployParams) => {
      if (!apiClients?.graphClient) return;

      setIsDeploying(true);
      setDeployingMessage(undefined);
      let sphereCreated = false;

      try {
        let targetSphereId: string;

        if (params.mode === 'create_new') {
          // Step 1: Create sphere of templateFor type under selected parent
          setDeployingMessage(t('marketplace.deployWizard.template.creatingStep'));

          const createResult = await createSphere(apiClients.graphClient, {
            schemaSystemName: definition?.templateFor || '',
            sphere: {
              properties: {
                name: params.newSphereName || '',
              },
              assignedPerspectives: [
                {
                  perspectiveProperties: { system_name: 'center_center' },
                  assigned: [{ sphere: { id: params.parentSphereId || '' } }],
                },
              ],
            },
          });

          const newSphereId = createResult.sphereId || createResult.sphere?.id;
          if (!newSphereId) {
            setDeployResult({
              success: false,
              message: t('marketplace.deployWizard.template.createFailed'),
            });
            return;
          }

          sphereCreated = true;
          targetSphereId = newSphereId;
        } else {
          targetSphereId = params.targetSphereId || '';
        }

        // Step 2: Deploy approach to the target sphere
        setDeployingMessage(t('marketplace.deployWizard.template.deployingStep'));

        const result = await deployApproach(apiClients.graphClient, {
          sphereId: targetSphereId,
          approachSystemName: systemName,
          deploymentInputValues: params.deploymentInputValues,
        });

        setDeployResult({
          success: result.status !== 'failed',
          message:
            result.status === 'failed'
              ? t('marketplace.deploy.error')
              : t('marketplace.deploy.success'),
        });
      } catch {
        setDeployResult({
          success: false,
          message: sphereCreated
            ? t('marketplace.deployWizard.template.deployAfterCreateFailed')
            : t('marketplace.deployWizard.template.createFailed'),
        });
      } finally {
        setIsDeploying(false);
        setDeployingMessage(undefined);
      }
    },
    [apiClients, systemName, definition?.templateFor, t]
  );

  // Target picker: user selected a sphere → continue deploy flow with that sphere
  const handleTargetSelect = useCallback(
    async (sphereId: string, sphereName: string) => {
      if (!apiClients?.graphClient) return;

      setShowTargetPicker(false);
      setPendingDeployApplicability(null);
      setTargetSphereId(sphereId);
      setTargetSphereName(sphereName);

      await proceedWithDeploy(sphereId);
    },
    [apiClients, proceedWithDeploy]
  );

  const handleTargetPickerCancel = useCallback(() => {
    setShowTargetPicker(false);
    setPendingDeployApplicability(null);
  }, []);

  const handleWizardCancel = useCallback(() => {
    setShowWizard(false);
    setDeploymentInputs([]);
    setDeployResult(null);
  }, []);

  if (loading && !approach) {
    return (
      <div className="marketplace-approach-detail">
        <nav className="marketplace-approach-detail__breadcrumb" aria-label="Breadcrumb">
          <button type="button" className="marketplace-approach-detail__breadcrumb-link" onClick={() => navigate('/marketplace')}>
            {t('marketplace.browse.title')}
          </button>
          <span className="marketplace-approach-detail__breadcrumb-sep" aria-hidden="true">›</span>
          <span className="marketplace-approach-detail__breadcrumb-current">...</span>
        </nav>
        <div className="marketplace-approach-detail__skeleton">
          <div className="marketplace-approach-detail__skeleton-header">
            <div className="marketplace-approach-detail__skeleton-icon" />
            <div className="marketplace-approach-detail__skeleton-header-text">
              <div className="marketplace-approach-detail__skeleton-bar marketplace-approach-detail__skeleton-bar--title" />
              <div className="marketplace-approach-detail__skeleton-bar marketplace-approach-detail__skeleton-bar--subtitle" />
              <div className="marketplace-approach-detail__skeleton-bar marketplace-approach-detail__skeleton-bar--meta" />
            </div>
          </div>
          <div className="marketplace-approach-detail__skeleton-bar marketplace-approach-detail__skeleton-bar--description" />
          <div className="marketplace-approach-detail__skeleton-bar marketplace-approach-detail__skeleton-bar--description" />
          <div className="marketplace-approach-detail__skeleton-section">
            <div className="marketplace-approach-detail__skeleton-bar marketplace-approach-detail__skeleton-bar--section-title" />
            <div className="marketplace-approach-detail__skeleton-card" />
          </div>
          <div className="marketplace-approach-detail__skeleton-section">
            <div className="marketplace-approach-detail__skeleton-bar marketplace-approach-detail__skeleton-bar--section-title" />
            <div className="marketplace-approach-detail__skeleton-card" />
          </div>
          <div className="marketplace-approach-detail__skeleton-section">
            <div className="marketplace-approach-detail__skeleton-bar marketplace-approach-detail__skeleton-bar--section-title" />
            <div className="marketplace-approach-detail__skeleton-card" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="marketplace-approach-detail">
        <nav className="marketplace-approach-detail__breadcrumb" aria-label="Breadcrumb">
          <button type="button" className="marketplace-approach-detail__breadcrumb-link" onClick={() => navigate('/marketplace')}>
            {t('marketplace.browse.title')}
          </button>
          <span className="marketplace-approach-detail__breadcrumb-sep" aria-hidden="true">›</span>
          <span className="marketplace-approach-detail__breadcrumb-current">...</span>
        </nav>
        <div className="marketplace-approach-detail__error">
          <span className="marketplace-approach-detail__error-icon">!</span>
          <h2 className="marketplace-approach-detail__error-title">
            {t('marketplace.approachDetail.errorLoading')}
          </h2>
          <p className="marketplace-approach-detail__error-description">
            {t('marketplace.approachDetail.errorDescription')}
          </p>
          <ButtonAtom
            variant="secondary"
            size="small"
            onClick={refetch}
            label={t('marketplace.common.retry')}
          />
        </div>
      </div>
    );
  }

  if (!approach) {
    return (
      <div className="marketplace-approach-detail">
        <nav className="marketplace-approach-detail__breadcrumb" aria-label="Breadcrumb">
          <button type="button" className="marketplace-approach-detail__breadcrumb-link" onClick={() => navigate('/marketplace')}>
            {t('marketplace.browse.title')}
          </button>
          <span className="marketplace-approach-detail__breadcrumb-sep" aria-hidden="true">›</span>
          <span className="marketplace-approach-detail__breadcrumb-current">...</span>
        </nav>
        <div className="marketplace-approach-detail__not-found">
          <span className="marketplace-approach-detail__not-found-icon">?</span>
          <h2 className="marketplace-approach-detail__not-found-title">
            {t('marketplace.approachDetail.notFound')}
          </h2>
          <p className="marketplace-approach-detail__not-found-description">
            {t('marketplace.approachDetail.notFoundDescription')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="marketplace-approach-detail">
      <nav className="marketplace-approach-detail__breadcrumb" aria-label="Breadcrumb">
        <button type="button" className="marketplace-approach-detail__breadcrumb-link" onClick={() => navigate('/marketplace')}>
          {t('marketplace.browse.title')}
        </button>
        <span className="marketplace-approach-detail__breadcrumb-sep" aria-hidden="true">›</span>
        {channelContext?.channelId && channelContext?.channelName && (
          <>
            <button type="button" className="marketplace-approach-detail__breadcrumb-link" onClick={() => navigate(`/marketplace/channels/${channelContext.channelId}`)}>
              {channelContext.channelName}
            </button>
            <span className="marketplace-approach-detail__breadcrumb-sep" aria-hidden="true">›</span>
          </>
        )}
        <span className="marketplace-approach-detail__breadcrumb-current">
          {getLocalizedText(approach.name)}
        </span>
      </nav>

      <div className="marketplace-approach-detail__content">
        <ApproachHeaderMolecule
          approach={approach}
          version={version}
          metrics={approach.metrics}
          onDeploy={handleDeploy}
          isDeploying={isDeploying && !showWizard}
          publisherName={publisherName || undefined}
          isSystemPublisher={isSystemPublisher}
          channelName={channelContext?.channelName}
          onChannelClick={
            channelContext?.channelId
              ? () =>
                  navigate(
                    `/marketplace/channels/${channelContext.channelId}`,
                  )
              : undefined
          }
          onBackToChannel={
            channelContext?.channelId
              ? () =>
                  navigate(
                    `/marketplace/channels/${channelContext.channelId}`,
                  )
              : undefined
          }
          deploymentMode={version?.definition?.deploymentMode}
          onManage={canManage ? () => setManageDialogOpen(true) : undefined}
        />

        <ApproachDetailTabsAtom
          activeTab={activeTab}
          onTabChange={setActiveTab}
          showWhatsIncludedTab={hasWhatsIncludedContent}
          communityCount={ratingsTotal}
        />

        {/* === Overview Tab === */}
        {activeTab === 'overview' && (
          <div className="marketplace-approach-detail__tab-content">
            {/* A. What you'll get */}
            {definition && outcomesCount > 0 && (
              <section
                className="marketplace-approach-detail__section"
                data-bucket="outcomes"
              >
                <OutcomeCardsMolecule
                  approach={definition}
                  header={
                    <SectionHeaderAtom
                      ord="A"
                      icon="pi-sparkles"
                      title={t(
                        'marketplace.approachDetail.overview.outcomes.sectionTitle',
                      )}
                      description={t(
                        'marketplace.approachDetail.overview.outcomes.sectionDescription',
                      )}
                    />
                  }
                />
              </section>
            )}

            {/* B. Where it can be deployed */}
            <section
              className="marketplace-approach-detail__section"
              data-bucket="applicability"
            >
              <ApplicabilityRendererMolecule
                filters={applicabilityFilters}
                header={
                  <SectionHeaderAtom
                    ord="B"
                    icon="pi-bullseye"
                    title={t(
                      'marketplace.approachDetail.overview.applicability.sectionTitle',
                    )}
                    description={t(
                      'marketplace.approachDetail.overview.applicability.sectionDescription',
                    )}
                  />
                }
              />
            </section>

            {/* C. Things to watch out for */}
            {edgeCases.length > 0 && (
              <section
                className="marketplace-approach-detail__section"
                data-bucket="edgeCases"
              >
                <SectionHeaderAtom
                  ord="C"
                  icon="pi-exclamation-triangle"
                  title={t(
                    'marketplace.approachDetail.overview.edgeCases.sectionTitle',
                  )}
                  description={t(
                    'marketplace.approachDetail.overview.edgeCases.sectionDescription',
                  )}
                />
                {edgeCases.map((ec) => (
                  <EdgeCaseCalloutMolecule
                    key={ec.id}
                    severity={ec.severity}
                    title={ec.title}
                    body={ec.body}
                  />
                ))}
              </section>
            )}

            {/* D. Before you deploy */}
            {hasReadiness && (
              <section
                className="marketplace-approach-detail__section"
                data-bucket="readiness"
              >
                <DeployReadinessChecklistMolecule
                  requiredInputs={requiredInputs}
                  optionalInputs={optionalInputs}
                  dependencies={dependencyItems}
                  header={
                    <SectionHeaderAtom
                      ord="D"
                      icon="pi-check-square"
                      title={t(
                        'marketplace.approachDetail.overview.readiness.sectionTitle',
                      )}
                      description={t(
                        'marketplace.approachDetail.overview.readiness.sectionDescription',
                      )}
                    />
                  }
                />
              </section>
            )}

            {/* E. How it deploys */}
            {deployBehavior && (
              <section
                className="marketplace-approach-detail__section"
                data-bucket="deployBehavior"
              >
                <DeployBehaviorMolecule
                  conflictStrategy={deployBehavior.conflictStrategy}
                  rollback={deployBehavior.rollback}
                  cascade={deployBehavior.cascade}
                  header={
                    <SectionHeaderAtom
                      ord="E"
                      icon="pi-send"
                      title={t(
                        'marketplace.approachDetail.overview.deployBehavior.sectionTitle',
                      )}
                      description={t(
                        'marketplace.approachDetail.overview.deployBehavior.sectionDescription',
                      )}
                    />
                  }
                />
              </section>
            )}
          </div>
        )}

        {/* === What's included? Tab === */}
        {activeTab === 'whatsIncluded' && (
          <div className="marketplace-approach-detail__tab-content">
            <WhatsIncludedReaderOrganism
              systemName={systemName}
              version={version?.version}
              depth={0}
              applicabilityFilters={applicabilityFilters}
            />
          </div>
        )}

        {/* === Community Tab === */}
        {activeTab === 'community' && (
          <div className="marketplace-approach-detail__tab-content">
            <div className="marketplace-approach-detail__ratings-section">
              <h2 className="marketplace-approach-detail__section-title">
                {t('marketplace.approachDetail.ratingsSection.title')}
              </h2>

              <div className="marketplace-approach-detail__ratings-content">
                <RatingSummaryMolecule
                  ratingAverage={approach.metrics?.rating_average || 0}
                  ratingCount={approach.metrics?.rating_count || 0}
                  distribution={approach.metrics?.rating_distribution || {}}
                />

                {(() => {
                  if (showRatingForm) {
                    return (
                      <RatingFormMolecule
                        initialStars={userRating?.stars}
                        initialTitle={userRating?.review?.title || ''}
                        initialBody={userRating?.review?.body || ''}
                        onSubmit={handleRatingSubmit}
                        onCancel={() => setShowRatingForm(false)}
                        isSubmitting={submitting}
                        isUpdate={!!userRating}
                        className="marketplace-approach-detail__rating-form"
                      />
                    );
                  }
                  if (userRating) {
                    return (
                      <div className="marketplace-approach-detail__user-rating">
                        <span className="marketplace-approach-detail__user-rating-label">
                          {t('marketplace.ratings.yourRating')}
                        </span>
                        <ButtonAtom
                          variant="secondary"
                          size="small"
                          onClick={() => setShowRatingForm(true)}
                          label={t('marketplace.ratings.editRating')}
                        />
                      </div>
                    );
                  }
                  return (
                    <ButtonAtom
                      variant="secondary"
                      onClick={() => setShowRatingForm(true)}
                      label={t('marketplace.approachDetail.ratingsSection.rateThisApproach')}
                    />
                  );
                })()}
              </div>

              {!ratingsLoading && reviewItems.length > 0 && (
                <ReviewListMolecule
                  reviews={reviewItems}
                  totalCount={ratingsTotal}
                  className="marketplace-approach-detail__reviews"
                />
              )}

              {ratingsHasMore && (
                <ButtonAtom
                  variant="text"
                  onClick={loadMoreRatings}
                  label={t('marketplace.common.loadMore')}
                />
              )}
            </div>
          </div>
        )}

        {/* Version selector — shown when multiple versions exist */}
        {versions.length > 1 && (
          <section className="marketplace-approach-detail__section marketplace-approach-detail__version-selector">
            <h2 className="marketplace-approach-detail__section-title">
              {t('marketplace.approachDetail.versions.title')}
            </h2>
            <VersionSelectorAtom
              versions={versions}
              currentVersion={version?.version}
              onChange={setSelectedVersion}
            />
          </section>
        )}
      </div>

      {/* Deploy error toast */}
      {deployError && (
        <div className="marketplace-approach-detail__deploy-toast marketplace-approach-detail__deploy-toast--error">
          <span>{deployError}</span>
          <button
            type="button"
            className="marketplace-approach-detail__deploy-toast-close"
            onClick={() => setDeployError(null)}
          >
            &times;
          </button>
        </div>
      )}

      {/* Deploy success toast (immediate deploy without wizard) */}
      {!showWizard && deployResult?.success && (
        <div className="marketplace-approach-detail__deploy-toast marketplace-approach-detail__deploy-toast--success">
          <span>{deployResult.message}</span>
          <button
            type="button"
            className="marketplace-approach-detail__deploy-toast-close"
            onClick={() => setDeployResult(null)}
          >
            &times;
          </button>
        </div>
      )}

      {/* Deploy Target Dialog */}
      {showTargetPicker && (
        <DeployTargetDialogOrganism
          open={showTargetPicker}
          onClose={handleTargetPickerCancel}
          applicability={pendingDeployApplicability}
          currentSphereId={currentSphereId}
          onSelect={handleTargetSelect}
        />
      )}

      {/* Approach Deploy Wizard overlay */}
      {showWizard && (
        <div className="marketplace-approach-detail__wizard-overlay">
          <div className="marketplace-approach-detail__wizard-container">
            <ApproachDeployWizardOrganism
              deploymentInputs={deploymentInputs}
              approachName={
                getLocalizedText(approach.name) || approach.system_name
              }
              targetSphereName={targetSphereName || undefined}
              templateFor={definition?.templateFor}
              applicability={pendingDeployApplicability}
              onDeploy={handleWizardDeploy}
              onTemplateDeploy={handleTemplateDeploy}
              onCancel={handleWizardCancel}
              isDeploying={isDeploying}
              deployingMessage={deployingMessage}
              deployResult={deployResult}
            />
          </div>
        </div>
      )}

      {/* Item management dialog — rendered when the current user has manage
          permission on a channel_item that publishes this approach. */}
      {manageableItem && (
        <ItemDetailDialogOrganism
          channelId={manageableItem.channel_id}
          item={{
            id: manageableItem.id,
            channel_system_name: manageableItem.channel_system_name,
            item_type: 'approach',
            item_system_name: approach.system_name,
            item_version: version?.version ?? null,
            item_metadata: {
              name: approach.name,
              description: approach.description,
              tags: approach.use_case_tags ?? [],
            },
            is_syndicated: manageableItem.is_syndicated,
            sort_order: 0,
            published_at: approach.created_at ?? new Date().toISOString(),
            published_by: approach.created_by ?? '',
            added_by: approach.created_by ?? null,
            unpublished_at: null,
            use_case_tags: approach.use_case_tags ?? [],
          }}
          visible={manageDialogOpen}
          onHide={() => setManageDialogOpen(false)}
          permissions={{
            canView: true,
            canManage,
            canDeploy: true,
            canFork: false,
          }}
          onItemUpdated={() => refetch()}
        />
      )}
    </div>
  );
};

export default ApproachDetailPageOrganism;
